import { AsyncPipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { BehaviorSubject, firstValueFrom, Subject } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { FileManagerComponent } from '@shared/components/file-manager/file-manager.component';
import { File } from '@shared/components/file-manager/state/file.model';
import { IconComponent } from '@shared/components/icon/icon.component';
import { CustomOverlayRef } from '@shared/components/overlay/custom-overlay-ref';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import { DocumentLibraryService } from '@shared/services/document-library.service';
import { DocumentType, EntityType, EventType } from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { EventTrackerService } from '@shared/store/event/event-tracker.service';
import { EventService } from '@shared/store/event/event.service';
import { INVOICE_STATUSES, InvoiceModel } from '@shared/store/invoice/invoice.model';
import { InvoiceService } from '@shared/store/invoice/invoice.service';
import { MainQuery } from '@shared/store/main/main.query';
import { MainStore } from '@shared/store/main/main.store';
import { OrganizationService } from '@shared/store/organization/organization.service';
import { batchPromises } from '@shared/utils';
import { Utils } from '@shared/utils/utils';

const invoiceType = DocumentType.DOCUMENT_INVOICE;
const supportingType = DocumentType.DOCUMENT_INVOICE_SUPPORT;

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe, FormsModule, IconComponent, TooltipDirective, FileManagerComponent],
  selector: 'aux-upload-documents-dialog',
  templateUrl: './upload-documents-dialog.component.html',
})
export class UploadDocumentsDialogComponent implements AfterViewInit {
  mainStore = inject(MainStore);
  organizationService = inject(OrganizationService);

  private destroyRef = inject(DestroyRef);
  private documentLibraryService = inject(DocumentLibraryService);
  private eventService = inject(EventTrackerService);
  private globalEventService = inject(EventService);
  private invoiceService = inject(InvoiceService);
  private mainQuery = inject(MainQuery);
  private overlayService = inject(OverlayService);

  documentTypes: Record<
    string,
    DocumentType.DOCUMENT_INVOICE | DocumentType.DOCUMENT_INVOICE_SUPPORT
  > = {};
  @ViewChild('fileManager') fileManager!: FileManagerComponent;
  files$ = new BehaviorSubject<File[]>([]);
  filesToDelete: File[] = [];
  hasChanges = false;
  invoice?: InvoiceModel;
  INVOICE = EntityType.INVOICE;
  invoiceId: string | undefined;
  invoiceNumber = '';
  loadingPreviousFiles$ = new BehaviorSubject(true);
  oneInvoiceSelected = false;
  pdf = 'application/pdf';
  ref = inject<CustomOverlayRef<unknown, { invoice: InvoiceModel }>>(
    CustomOverlayRef<unknown, { invoice: InvoiceModel }>
  );
  updateValidation = new Subject<File | null>();
  vendor = '';

  // eslint-disable-next-line @typescript-eslint/no-misused-promises
  async ngAfterViewInit() {
    await this.fileManager.fetchFiles();

    this.fileManager.fileQuery
      .selectAll()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((files: File[]) => {
        this.loadingPreviousFiles$.next(false);
        this.files$.next(files);
      });

    this.updateValidation
      .pipe(
        switchMap((passedFile: File | null) => {
          return this.files$.pipe(map((files) => ({ files, passedFile })));
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ files, passedFile }) => {
        let assignOtherPdfsToSupport = false;
        let fileChangedDesignation = false;

        // this will only be called if the file has changed designation so checking for passedFile is a sufficient condition
        if (passedFile) {
          fileChangedDesignation = true;

          if (this.documentTypes[passedFile.id] === invoiceType) {
            this.invoiceService.invoiceDesignationChanged$.next(true);
            assignOtherPdfsToSupport = true;
          }
        }

        files.forEach((file) => {
          if (this.documentTypes[file.id] === undefined) {
            this.documentTypes[file.id] =
              file.document_type_id === invoiceType && file.uploaded ? invoiceType : supportingType;
          }

          if (
            assignOtherPdfsToSupport &&
            this.documentTypes[file.id] === invoiceType &&
            file.id !== passedFile?.id
          ) {
            this.documentTypes[file.id] = supportingType;
          }
        });
        this.hasChanges =
          files.some((file) => !file.uploaded) ||
          !!this.filesToDelete.filter((file) => file.uploaded).length ||
          fileChangedDesignation;
      });

    this.updateValidation.next(null);
  }

  constructor() {
    if (this.ref.data) {
      this.invoice = this.ref.data.invoice;
      this.invoiceId = this.invoice.id;
      this.vendor = this.invoice.organization.name;
      this.invoiceNumber = this.invoice.invoice_no || '—';
    }
  }

  getFilePath = (type?: typeof invoiceType | typeof supportingType) => {
    const trialId = this.mainQuery.getValue().trialKey;

    if (this.invoice && type) {
      return `trials/${trialId}/vendors/${this.invoice.organization.id}/invoices/${this.invoice.id}/${type}/`;
    }

    return `trials/${trialId}/vendors/`;
  };

  getTooltip(isInvoice = false, file: File) {
    if (isInvoice && !this.isFilePdf(file)) {
      return 'Invoice file type must be PDF';
    }

    return '';
  }

  isFilePdf(file: File) {
    return file.bucket_key.endsWith('.pdf');
  }

  // eslint-disable-next-line max-lines-per-function
  onSave = async () => {
    if (this.fileManager) {
      await batchPromises(this.filesToDelete, (file) => this.fileManager.removeFile(file));

      // filtering out uploaded files since we don't need to upload or modify them
      const files = this.fileManager.fileQuery.getAll().filter((f) => !f.uploaded);

      const filesWithChangedDocumentType = this.fileManager.fileQuery.getAll().filter((f) => {
        return f.uploaded && f.document_type_id !== this.documentTypes[f.id];
      });

      if (filesWithChangedDocumentType.length) {
        await this.documentLibraryService.updateDocuments(
          filesWithChangedDocumentType.map((f) => ({
            document_type_id: this.documentTypes[f.id],
            id: f.id,
          }))
        );
      }

      let bucketKeyOfNewInvoicePdf =
        filesWithChangedDocumentType.find((f) => this.documentTypes[f.id] === invoiceType)
          ?.bucket_key || '';

      for (const file of files) {
        const documentType = this.documentTypes[file.id];
        const bucket_key = `${this.getFilePath(documentType)}${file.bucket_key}`;

        if (documentType === invoiceType) {
          bucketKeyOfNewInvoicePdf = bucket_key;
        }

        this.fileManager.fileStore.update(file.id, {
          ...file,
          bucket_key: bucket_key,
          document_type_id: this.documentTypes[file.id],
        });
      }

      const filesSuccess = await this.fileManager.fileService.uploadFiles({
        filterLocalFilesByUploadedFlag: undefined,
        insertDocument: true,
        is_metadata_editable: true,
        metadata: {
          documentType: DocumentType.DOCUMENT_INVOICE,
          entity_id: this.invoice !== undefined ? this.invoice.id : '',
          entity_type_id: EntityType.INVOICE,
          status: INVOICE_STATUSES.STATUS_IN_QUEUE,
          vendor: this.invoice?.organization.id || '',
        },
        overrideFilePath: undefined,
        removeFilesOnPartialFailure: undefined,
        showSuccess: false,
      });

      if (filesSuccess) {
        if (bucketKeyOfNewInvoicePdf) {
          const completion_tracker_id = Utils.uuid();
          const {
            data,
            errors: eventErrors,
            success: eventSuccess,
          } = await firstValueFrom(
            this.globalEventService.processEvent$({
              bucket_key: `public/${bucketKeyOfNewInvoicePdf}`,
              entity_id: this.invoice?.id || '',
              entity_type: EntityType.INVOICE,
              payload: JSON.stringify({
                completion_tracker_id,
              }),
              type: EventType.INVOICE_DOCUMENT_UPLOADED,
            })
          );

          // eslint-disable-next-line max-depth
          if (eventSuccess) {
            // eslint-disable-next-line max-depth
            if (data) {
              void this.eventService.trackEvent(completion_tracker_id);
            }

            this.overlayService.success(`Invoice successfully updated!`);
          } else {
            this.overlayService.error(`Invoice could not be processed ${eventErrors}`);
          }
        }

        this.ref.close(this.fileManager.fileQuery.getAll());
      }
    }
  };

  removeFile(file: File) {
    this.filesToDelete.push(file);
    this.fileManager.fileService.removeFromStore(file);
  }
}
